import { useId, useRef, useState, type ReactNode } from "react";
import { Button } from "./Button.js";
import { Modal } from "./Modal.js";
import { Stack } from "./Stack.js";
import { Inline } from "./Inline.js";

/**
 * Sprint 61 — shared hard-delete confirmation primitive.
 *
 * The Owner's request (2026-05-14) was a single Delete + Confirm
 * pattern across vehicles, vehicle groups, devices, and drivers.
 * Identifier-eintippen confirmation was chosen over a plain
 * Yes/No modal because hard-delete is irreversible and the
 * higher reibung is appropriate. Archive remains a separate
 * action; this primitive does not replace it.
 *
 * GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01 hardened
 * the presentation without touching the safety contract. Previously the
 * open dialog **replaced the trigger in the layout flow** while still
 * announcing `role="dialog" aria-modal="true"` — an inline pseudo-modal
 * with no scrim, no focus trap, no Escape and no focus return, which
 * also inherited its parent's width (a `Stack` child stretched it
 * across the whole content column — the Owner's FLEET-UX-005 finding).
 * It now renders through the shared `Modal`, so:
 *   - the trigger stays in place and keeps its cluster's size,
 *   - the dialog is a real overlay with a scrim and a focus trap,
 *   - Escape and Cancel close it (Escape is suppressed while the
 *     delete request is in flight),
 *   - focus starts in the identifier input and returns to the trigger.
 * The identifier gate itself — exact, case-sensitive re-entry of the
 * entity identifier — is unchanged.
 *
 * Contract:
 *   - The trigger renders as a danger-style Button labelled
 *     `triggerLabel` (typically the localised "Delete" string).
 *   - Clicking the trigger opens a modal dialog; the operator
 *     must type the entity's identifier verbatim (e.g. an IMEI,
 *     plate number, group name, driver name) before the actual
 *     Delete button becomes enabled.
 *   - `onConfirm` is called only after the typed value matches
 *     `identifierExpected` AND the operator clicks the now-
 *     enabled Delete button. Errors thrown by `onConfirm` are
 *     surfaced inline so the operator sees refusal reasons (e.g.
 *     EntityInUseError with linked-entities) without losing the
 *     dialog.
 *   - `archiveLink` is an optional ReactNode (typically a
 *     button or anchor wired to the soft-archive flow) shown
 *     under the confirmation row so the operator can step back
 *     into the safer path.
 *
 * Anti-patterns this primitive blocks (per AGENTS.md §6B.5):
 *   - "Smallest superficial diff" — every call site uses the same
 *     component; the verb cannot drift between surfaces.
 *   - "Sender-only fix" — refusal messages from the API surface
 *     (EntityInUseError, ForbiddenError) render to the operator
 *     in the same dialog that triggered the action.
 *
 * The Owner-stated requirement that archive remains untouched is
 * enforced by NOT calling any archive-side mutation from this
 * primitive — the parent supplies both buttons independently.
 */
export interface DeleteWithConfirmProps {
  /** Localised label on the danger Button that opens the dialog (e.g. "Delete"). */
  readonly triggerLabel: string;
  /**
   * Localised label for the entity-kind shown to the operator (e.g. "vehicle",
   * "group", "driver", "device"). Capitalised by the caller; the primitive
   * displays it verbatim.
   */
  readonly entityLabel: string;
  /**
   * Localised name of the identifying field the operator must retype to
   * confirm (e.g. "IMEI", "Plate", "Name").
   */
  readonly identifierFieldLabel: string;
  /**
   * The literal identifier value the operator must type back. The
   * comparison is case-sensitive and trimmed; whitespace inside the
   * value is preserved exactly.
   */
  readonly identifierExpected: string;
  /**
   * Localised body text shown above the input. Should explain the
   * permanence of the action and any cascade behaviour (e.g.
   * "Members of this group will also be deleted.").
   */
  readonly warningBody: string;
  /**
   * Localised confirm button label (e.g. "Delete permanently"). The
   * button stays disabled until the identifier match is exact.
   */
  readonly confirmLabel: string;
  /** Localised cancel button label (e.g. "Cancel"). */
  readonly cancelLabel: string;
  /** Localised "type to confirm" hint shown beside the input. */
  readonly typeToConfirmHint: string;
  /**
   * Optional archive affordance rendered alongside the cancel button
   * — typically a localised "Archive instead" link/button. Hidden
   * when omitted.
   */
  readonly archiveAffordance?: ReactNode;
  /**
   * Optional localised string templates rendering structured server
   * errors. The primitive turns `entity_in_use` into a list under
   * the dialog; the caller passes the message templates.
   */
  readonly errorMessages?: {
    readonly forbidden?: string;
    readonly entityInUse?: string;
    /**
     * Appended to `entityInUse` when the server reports that the
     * blocker list was capped — e.g. "Additional linked records also
     * block permanent deletion."
     */
    readonly entityInUseTruncated?: string;
    readonly notFound?: string;
    readonly generic?: string;
  };
  /**
   * Test id prefix the caller passes to anchor `<button>` / `<input>`
   * targets in playwright / vitest tests. The primitive appends the
   * suffixes `-trigger`, `-confirm`, `-cancel`, `-identifier-input`,
   * `-dialog`, `-error`.
   */
  readonly testIdPrefix?: string;
  /**
   * Size of the trigger button. Defaults to `md` so every Sprint-61
   * call site is unchanged; FLEET action clusters pass `sm` so the
   * destructive trigger matches its neighbours.
   */
  readonly triggerSize?: "sm" | "md" | "lg";
  /**
   * Async delete handler. Throws on refusal (the primitive renders
   * `EntityInUseError`-shaped or generic errors). On resolve, the
   * dialog closes.
   */
  readonly onConfirm: () => Promise<void>;
}

interface StructuredApiError {
  readonly error?: string;
  readonly status?: number;
  readonly message?: string;
  readonly linkedEntities?: ReadonlyArray<{
    readonly type: string;
    readonly id: string;
    readonly label?: string;
  }>;
  /**
   * W3 — the server caps the blocker enumeration. `true` means more
   * blocking records exist than are listed, and the operator must be
   * told: otherwise they clear the visible ones and are refused again
   * with no explanation. `undefined` means the server could not
   * establish it (the FK-race path) and is deliberately NOT treated as
   * `false`.
   */
  readonly linkedEntitiesTruncated?: boolean;
}

function readApiError(err: unknown): StructuredApiError | null {
  if (err === null || typeof err !== "object") return null;
  const obj = err as Record<string, unknown>;
  const status = typeof obj.status === "number" ? obj.status : undefined;
  const body = (obj.body ?? obj.payload ?? obj) as Record<string, unknown> | undefined;
  if (!body) return { ...(status !== undefined ? { status } : {}) };
  const code = typeof body.error === "string" ? body.error : undefined;
  const message = typeof body.message === "string" ? body.message : undefined;
  const linkedRaw = Array.isArray(body.linkedEntities) ? body.linkedEntities : undefined;
  const linkedEntities = linkedRaw
    ?.filter((x): x is Record<string, unknown> => x !== null && typeof x === "object")
    .map((x) => ({
      type: typeof x.type === "string" ? x.type : "unknown",
      id: typeof x.id === "string" ? x.id : "",
      ...(typeof x.label === "string" ? { label: x.label } : {}),
    }));
  const truncated =
    typeof body.linkedEntitiesTruncated === "boolean" ? body.linkedEntitiesTruncated : undefined;
  const result: StructuredApiError = {
    ...(code !== undefined ? { error: code } : {}),
    ...(status !== undefined ? { status } : {}),
    ...(message !== undefined ? { message } : {}),
    ...(linkedEntities !== undefined ? { linkedEntities } : {}),
    ...(truncated !== undefined ? { linkedEntitiesTruncated: truncated } : {}),
  };
  return result;
}

export function DeleteWithConfirm(props: DeleteWithConfirmProps) {
  const {
    triggerLabel,
    // Sprint 62 (C1) — entityLabel kept in DeleteWithConfirmProps
    // for backwards compatibility with Sprint 61 call sites, but
    // no longer rendered: the i18n triggerLabel already includes
    // the entity word, so the previous heading produced
    // "Delete device device". See the heading site below.
    identifierFieldLabel,
    identifierExpected,
    warningBody,
    confirmLabel,
    cancelLabel,
    typeToConfirmHint,
    archiveAffordance,
    errorMessages,
    testIdPrefix,
    triggerSize = "md",
    onConfirm,
  } = props;

  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [pending, setPending] = useState(false);
  const [errorState, setErrorState] = useState<StructuredApiError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const warningId = `${inputId}-warning`;

  const id = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  const identifierMatches = typed === identifierExpected;

  function close() {
    setOpen(false);
    setTyped("");
    setErrorState(null);
  }

  async function handleConfirm() {
    if (!identifierMatches || pending) return;
    setErrorState(null);
    setPending(true);
    try {
      await onConfirm();
      close();
    } catch (err) {
      setErrorState(readApiError(err) ?? { message: errorMessages?.generic ?? "Delete failed." });
    } finally {
      setPending(false);
    }
  }

  function renderError(): ReactNode {
    if (!errorState) return null;
    const code = errorState.error;
    if (code === "forbidden" && errorMessages?.forbidden) {
      return errorMessages.forbidden;
    }
    if (code === "entity_in_use" && errorMessages?.entityInUse) {
      const links = errorState.linkedEntities ?? [];
      const labels = links.map((l) => l.label ?? l.id).join(", ");
      const base =
        links.length > 0 ? `${errorMessages.entityInUse}: ${labels}` : errorMessages.entityInUse;
      /*
        Append the truncation notice ONLY when the server said it
        truncated. An unknown truncation state (the FK-race path) is not
        rendered as a completeness claim in either direction — the
        operator is simply not told something we did not establish.
      */
      return errorState.linkedEntitiesTruncated === true && errorMessages.entityInUseTruncated
        ? `${base} ${errorMessages.entityInUseTruncated}`
        : base;
    }
    // GT-DEVICE-LIFECYCLE-MOVE-DELETE-05 — the admin device routes return
    // the typed code `device_not_found` (not the generic `not_found`); map
    // both so a 404 shows the actionable not-found copy, never the generic
    // "Delete failed. Please retry." fallback.
    if ((code === "not_found" || code === "device_not_found") && errorMessages?.notFound) {
      return errorMessages.notFound;
    }
    return errorState.message ?? errorMessages?.generic ?? "Delete failed.";
  }

  return (
    <>
      {/*
        The trigger stays mounted while the dialog is open. Before the
        FLEET hardening the dialog replaced it, which both broke focus
        return (the element to return focus to no longer existed) and
        let the confirmation inherit the parent container's width.
      */}
      <Button
        variant="danger"
        size={triggerSize}
        type="button"
        onClick={() => setOpen(true)}
        data-testid={id("trigger")}
      >
        {triggerLabel}
      </Button>

      <Modal
        open={open}
        onClose={close}
        /*
          Sprint 62 (C1) — the heading previously rendered
          `{triggerLabel} {entityLabel}` which produced
          "Delete device device" / "Delete group group" / etc.
          because the i18n key for every action.delete already
          included the entity word (e.g. "Delete device"). Render
          only the triggerLabel; `entityLabel` is kept in the
          props interface for backwards compatibility with the
          Sprint 61 callers and may be removed in a later sweep.
        */
        title={triggerLabel}
        describedBy={warningId}
        dismissible={!pending}
        initialFocusRef={inputRef}
        {...(id("dialog") ? { dataTestId: id("dialog")! } : {})}
      >
        <Stack gap={3}>
          <span
            id={warningId}
            style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-sm)" }}
          >
            {warningBody}
          </span>
          <Stack gap={1}>
            <label
              htmlFor={inputId}
              style={{
                fontSize: "var(--gt-sys-font-size-sm)",
                fontWeight: "var(--gt-sys-font-weight-semibold)",
              }}
            >
              {identifierFieldLabel}{" "}
              <span
                style={{
                  color: "var(--gt-sys-text-muted)",
                  fontWeight: "var(--gt-sys-font-weight-regular)",
                }}
              >
                {typeToConfirmHint}
              </span>
            </label>
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
              data-testid={id("identifier-input")}
              autoComplete="off"
              aria-describedby={warningId}
              aria-invalid={typed.length > 0 && !identifierMatches ? true : undefined}
              style={{
                fontSize: "var(--gt-sys-font-size-sm)",
                paddingBlock: "var(--gt-sys-space-1)",
                paddingInline: "var(--gt-sys-space-2)",
                border: "1px solid var(--gt-sys-border)",
                borderRadius: "var(--gt-sys-radius-sm)",
                background: "var(--gt-sys-surface)",
                color: "var(--gt-sys-text)",
                fontFamily: "var(--gt-sys-font-family-mono, monospace)",
              }}
            />
          </Stack>
          {errorState ? (
            <span
              role="alert"
              data-testid={id("error")}
              style={{ color: "var(--gt-sys-danger)", fontSize: "var(--gt-sys-font-size-sm)" }}
            >
              {renderError()}
            </span>
          ) : null}
          <Inline gap={2} justify="space-between" wrap>
            {archiveAffordance ?? <span />}
            <Inline gap={2}>
              <Button
                variant="secondary"
                type="button"
                onClick={close}
                disabled={pending}
                data-testid={id("cancel")}
              >
                {cancelLabel}
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  void handleConfirm();
                }}
                disabled={!identifierMatches || pending}
                data-testid={id("confirm")}
              >
                {confirmLabel}
              </Button>
            </Inline>
          </Inline>
        </Stack>
      </Modal>
    </>
  );
}
